import {
  type NonPharmaItem,
  RXDB_NON_PHARMA_ITEM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/non-pharma-item";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { Box, CircularProgress, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useRxData } from "rxdb-hooks";
import { useDebouncedCallback } from "use-debounce";

const FUSE_OPTIONS: Fuse.IFuseOptions<NonPharmaItem> = {
  keys: [
    { name: "title", weight: 0.7 },
    { name: "brand", weight: 0.2 },
    { name: "keywords", weight: 0.1 },
  ],
  threshold: 0.3,
  includeMatches: false,
  ignoreLocation: true,
};

const MAX_RESULTS = 25;

export type NonPharmaItemAutocompleteValue = {
  id: string;
  title: string;
  brand?: string;
};

type Props = {
  value: NonPharmaItemAutocompleteValue | null;
  onChange: (item: NonPharmaItemAutocompleteValue | null) => void;
  disabled?: boolean;
};

const labelFor = (option: NonPharmaItemAutocompleteValue) =>
  option.brand ? `${option.title} (${option.brand})` : option.title;

export const NonPharmaItemAutocomplete: FC<Props> = ({
  value,
  onChange,
  disabled,
}) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState("");
  const setQueryDebounced = useDebouncedCallback(setQuery, 200);

  const { result: items, isFetching } = useRxData<NonPharmaItem>(
    RXDB_NON_PHARMA_ITEM_COLLECTION_NAME,
    (collection) => collection.find({ selector: {} }),
  );

  const {
    replicationState: {
      nonPharmaItem: { isPulling },
    },
  } = useRxDBContext();

  const loading = isFetching || isPulling;

  const fuse = useMemo(
    () => new Fuse(items?.map((d) => d._data) ?? [], FUSE_OPTIONS),
    [items],
  );

  const options: NonPharmaItemAutocompleteValue[] = useMemo(() => {
    if (!query.trim()) return [];
    return fuse.search(query, { limit: MAX_RESULTS }).map((result) => ({
      id: result.item.id,
      title: result.item.title,
      brand: result.item.brand,
    }));
  }, [fuse, query]);

  return (
    <Autocomplete<NonPharmaItemAutocompleteValue>
      value={value}
      options={options}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option: NonPharmaItemAutocompleteValue | string) =>
        typeof option === "string" ? option : labelFor(option)
      }
      getOptionSelected={(a, b) => a.id === b.id}
      filterOptions={(opts) => opts}
      noOptionsText={
        query.trim() ? (
          <FormattedMessage
            id="nonPharmaDispense.search.noResults"
            defaultMessage="No matching non-pharma items"
          />
        ) : (
          <FormattedMessage
            id="nonPharmaDispense.search.typeToSearch"
            defaultMessage="Start typing to search the non-pharma catalogue"
          />
        )
      }
      onInputChange={(_, v) => setQueryDebounced(v)}
      onChange={(_, v) => onChange(typeof v === "string" || !v ? null : v)}
      renderOption={(option) => (
        <Box display="flex" flexDirection="column">
          <span>{option.title}</span>
          {option.brand && (
            <span style={{ fontSize: "0.75rem", color: "#666" }}>
              {option.brand}
            </span>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={formatMessage({
            id: "nonPharmaDispense.search.label",
            defaultMessage: "Find non-pharma item",
          })}
          variant="outlined"
          fullWidth
          data-testid="non-pharma-search"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
